<template>
  <!-- Szablon niestandardowy -->
  <div v-if="customTemplate && product && firstVariant" v-html="generatedHtml"></div>

  <!-- Domyślna struktura (renderuj tylko gdy mamy komplet danych) -->
  <div v-else-if="product && firstVariant" class="q-row products__promoted-product-container">
    <div class="q-col-sm-12 products__promoted-product-container-inner">
      <div class="products__promoted-product q-p-1 q-position-relative q-d-flex q-flex-wrap">
        <div class="products__promoted-product-image-container q-col-4 q-col-lg-12 q-p-0">
          <img class="products__promoted-product-image q-p-1" :src="staticImage" :alt="product.title">
        </div>
        <div class="products__promoted-product-info q-col-8 q-col-lg-12 q-px-2 q-text-black q-text-start">
          <p class="products__promoted-product-title q-m-0 q-fw-bold">
            <span v-html="highlightedTitle"></span>
          </p>
          <AutocompletePriceBox :product="firstVariant" />
          <a :href="actualLink" @click="handleClick" class="q-stretched-link"></a>
        </div>
        <a :href="actualLink" @click="handleClick" class="q-stretched-link"></a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue';
import { defineProps, ref, inject, computed, watchEffect } from 'vue';
import { generateStaticImage, addDisplayMacros, formatPrice, parsePrice, getDiscount, getRealPrice } from '@/core/utils';
import { createTemplate } from '@/core/Template';
import type { SmartSearchConfigInterface, ProductInterface } from '@/core/types';
import { useSearchQueryStore } from '@/store/SearchQueryStore';
import { storeToRefs } from 'pinia';
import { highlightText } from '@/core/highlight';

const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const { width, height } = SmartSearchConfig.value.onfocus.topProduct.productImageSize;
const { useQuarticImages } = SmartSearchConfig.value.serp.productImageSize;
const customTemplate = SmartSearchConfig?.value?.serp?.customComponents?.topProduct || null;

const props = defineProps<{
  promotedProduct: ProductInterface | null
}>();

// Źródła danych
const product = computed(() => props.promotedProduct ?? null);
const firstVariant = computed(() => product.value?.variants?.[0] ?? null);

const actualLink = ref<string>('');
const clickLink  = ref<string>('');

watchEffect(() => {
  actualLink.value = product.value?.link ?? '';
  clickLink.value  = product.value?.link_click ?? '';
});

const searchQueryStore = useSearchQueryStore();
const { searchQuery } = storeToRefs(searchQueryStore);

const highlightedTitle = computed(() =>
    highlightText(product.value?.title ?? '', searchQuery.value ?? '', 'products__promoted-product-title-highlight')
);

// Obraz
const staticImage = computed(() => {
  const imageLink = firstVariant.value?.image_link || 'https://static.quartic.com.pl/img/no-image/no-image.png';
  if (!useQuarticImages) return imageLink;
  return generateStaticImage(imageLink, width, height);
});

// Klik
const handleClick = () => {
  if (clickLink.value) actualLink.value = clickLink.value.replace(/^http:/, 'https:');
};

// --- Makra i custom template ---
const priceData = computed(() => {
  if (!firstVariant.value) return null;
  const customMapping = window._QONizer?.customSmartSearchConfig?.customPriceMapping;
  
  const priceKey = customMapping?.price || 'price';
  const oldPriceKey = customMapping?.oldPrice || 'old_price';
  const salePriceKey = customMapping?.salePrice || 'sale_price';

  const v = firstVariant.value;
  const rawPrice = v[priceKey];
  const rawOldPrice = v[oldPriceKey];
  const rawSalePrice = v[salePriceKey];

  const priceToParse = (typeof rawPrice === 'string' || typeof rawPrice === 'number') ? rawPrice : v.price;
  const oldPriceToParse = (typeof rawOldPrice === 'string' || typeof rawOldPrice === 'number') ? rawOldPrice : v.old_price;
  const salePriceToParse = (typeof rawSalePrice === 'string' || typeof rawSalePrice === 'number') ? rawSalePrice : v.sale_price;

  const secondPrice = (oldPriceToParse || salePriceToParse) as string | number | null;
  const parsedPrice = parsePrice(priceToParse as string | number | null);
  const parsedOldPrice = parsePrice(secondPrice);
  const realPrices = getRealPrice(parsedPrice, parsedOldPrice);
  
  return {
    price: realPrices.price,
    oldPrice: realPrices.oldPrice,
    parsedPrice,
    parsedOldPrice
  };
});

const omnibusPrice = computed(() => firstVariant.value ? parsePrice(firstVariant.value.omnibus_price) : null);

const discountValue = computed(() => {
  if (!priceData.value) return null;
  const { price, oldPrice, parsedPrice, parsedOldPrice } = priceData.value;
  if (parsedPrice && parsedOldPrice && parsedPrice >= parsedOldPrice) {
    return null;
  }
  return getDiscount(price, oldPrice);
});

const discount = computed(() => discountValue.value);

const formattedPrice = computed(() => priceData.value ? formatPrice(priceData.value.price, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals) : '');
const formattedPriceOld = computed(() => priceData.value ? formatPrice(priceData.value.oldPrice, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals) : '');
const formattedOmnibusPrice = computed(() => omnibusPrice.value !== null ? formatPrice(omnibusPrice.value, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals) : '');

const prepareClearLink = (link: string, clickLink: string) =>
    ` href="${link}" data-href="${clickLink.replace(/^http:/, 'https:')}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;

const generatedHtml = computed(() => {
  if (!customTemplate || !product.value || !firstVariant.value || !priceData.value) {
    return '';
  }

  // klon (żeby nie mutować oryginalnego wariantu z props)
  const v = { ...firstVariant.value };

  v.discount = discount.value ? `- ${discount.value}%` : null;
  v.image_link_cdn = staticImage.value;
  v.price_formatted = formattedPrice.value;
  v.price_number = priceData.value.price;
  v.old_price_formatted = formattedPriceOld.value;
  v.old_price_number = priceData.value.oldPrice;
  v.omnibus_price_formatted = formattedOmnibusPrice.value;
  v.omnibus_price_number = omnibusPrice.value;
  v.id = product.value!.id;
  v.link_click = clickLink.value;
  v.clear_link = prepareClearLink(actualLink.value, clickLink.value);

  addDisplayMacros(v);

  return createTemplate(customTemplate, v, [v]);
});
</script>

<style>
img[lazy=loaded] {
  animation-duration: 200ms;
  animation-fill-mode: both;
  animation-name: fadeIn;
}

.products__promoted-product-image {
  height: auto;
  width: auto;
  margin: auto;
  min-width: 100px;
  max-width: 100%;
  max-height: 100%;
}

.products__promoted-product-title {
  text-align: center;
}

.products__promoted-product-container .single-product__price-box-container {
  text-align: center;
}

.products__promoted-product {
  display: flex !important;
  align-content: center;
  flex-wrap: wrap;
  flex-direction: column;
  min-height: 100%;
  justify-content: space-around;
}

.products__promoted-product-container {
  min-height: calc(100% - 50px);
}

@media (max-width: 991.98px) {
  .products__promoted-product-container { min-height: unset; }
  .products__promoted-product {
    align-content: unset;
    flex-wrap: unset;
    flex-direction: unset;
    min-height: unset;
    justify-content: unset;
  }
  .products__promoted-product-image { max-height: 135px; }
  .products__promoted-product { border-bottom: 1px solid #f0f2f6; }
  .products__promoted-product-title { text-align: left; }
  .products__promoted-product-container .single-product__price-box-container { text-align: left; }
}

.products__promoted-product { transition: background-color 0.3s ease; }
.products__promoted-product:hover { background-color: #f2f2f2; }

.products__promoted-product-title {
  font-size: 0.75em;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

mark.products__promoted-product-title-highlight {
  background: #dc35453b!important;
  padding: 0 !important;
}
</style>
